import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import differenceInDays from 'date-fns/difference_in_days';

import ProductCover from 'Components/Utility/ProductCover';
import store from 'Redux/store';
import BadgeSlash from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/BadgeSlash';
import WishlistButton from 'Components/Wishlist/WishlistButton';
import useViewport from 'Hooks/useViewport';
import TagSection from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/TagSection';
import PlatformIcons from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/PlatformIcons';
import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import 'Layout/NavBar/MegaMenu/FeaturedTab.scss';

const CountdownToLaunch = lazy(() => import(/* webpackChunkName: "CountdownToLaunch" */ 'Components/Product/Promotions/CountdownToLaunch'));

export default function FeaturedTab({
  hit,
  setMegaMenuTab,
  tab,
  section,
}) {
  const { isAboveMd, isBelowXl, isAboveLg } = useViewport();

  if (!hit) return false;

  let validDrm = '';
  let filteredDrm = [];
  if (hit && hit.drm) filteredDrm = hit.drm.filter(d => d !== 'voucher');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];

  const logClick = () => {
    store.dispatch({
      type: 'frontend/click/CLICK_INTERACTION',
      payload: { section: 'MegaMenuFeaturedTab', value: `/${hit.type}/${hit.slug}` },
    });

    setMegaMenuTab('');
  };

  const isPreorder = (hit.release_date * 1000) > Date.now();

  let earlyAccess = false;
  if (hit.preorder_playable_from) {
    if (
      (hit.release_date * 1000) > Date.now() &&
      (hit.preorder_playable_from * 1000) < (hit.release_date * 1000)
    ) {
      earlyAccess = true;
    }
  }

  let prerelease = false;
  const today = new Date().getTime();
  const releaseTime = hit.release_date * 1000;

  if (releaseTime > today) {
    prerelease = true;
  }

  let daysLeft = differenceInDays(new Date(hit.release_date * 1000), new Date());
  if (earlyAccess) {
    daysLeft = differenceInDays(new Date(hit.preorder_playable_from * 1000), new Date());
  }

  return (
    <div className={classNames(`ProductsMegaMenu__tab ProductsMegaMenu__tab--${section} FeaturedTab`, { 'ProductsMegaMenu__tab--visible': tab === section })}>
      <div className="FeaturedTab__countdown">
        {prerelease && daysLeft < 30 && daysLeft >= 0 &&
          <Suspense>
            <CountdownToLaunch
              releaseDate={hit.release_date * 1000}
              playableFrom={hit.preorder_playable_from * 1000}
              earlyAccess={earlyAccess}
            />
          </Suspense>
        }
      </div>
      <div className={classNames('FeaturedCarousel__slideContainer FeaturedTab__wrapper')}>
        <div className="FeaturedTab__image">
          <Link
            to={`/${hit.type}/${hit.slug}`}
            onClick={() => logClick()}
          >
            <ProductCover
              cover={hit.cover}
              alt={`Product cover for ${hit.name}`}
              displayType={hit.display_type}
              sizesOverride={'(min-width: 1520px) 883px, (min-width: 1200px) 740px, (min-width: 992px) 620px, 95vw'}
            />
          </Link>

        </div>
        <div className="FeaturedTab__contentWrapper">
          <div className="FeaturedTab__contentWrapperLink faux-block-link">
            <div className="FeaturedTab__content">
              <div className="FeaturedTab__content__topContainer">
                <div className="FeaturedTab__badgeWishlistContainer">
                  {(Math.abs(daysLeft) <= 2 || isPreorder) ? (
                    <BadgeSlash
                      type={isPreorder ? 'preOrder' : 'justLaunched'}
                      releaseDate={hit.release_date}
                      earlyAccessDate={hit.preorder_playable_from}
                    />
                  ) : (
                    <div />
                  )}
                  <WishlistButton
                    noText={window.locale !== 'en' && isBelowXl}
                    reducedText={window.locale !== 'en'}
                    slug={hit.slug}
                    productId={hit.product_id}
                    product={hit}
                  />
                </div>

                <h2 className="FeaturedTab__content__title">
                  <Link
                    to={`/${hit.type}/${hit.slug}`}
                    className="faux-block-link__overlay-link"
                    onClick={() => logClick()}
                  >
                    {hit.name}
                  </Link>
                </h2>

                <div className="FeaturedTab__section">
                  <TagSection tags={(hit.categories || []).slice(0, (window.locale !== 'en' ? 3 : 4))} />
                </div>

                {isAboveLg &&
                  <div className="FeaturedTab__section FeaturedTab__content__shortDesc">
                    {(hit.sdescs ? hit.sdescs[(window.locale || 'en')] : null) || hit.sdesc}
                  </div>
                }

                <div className="FeaturedTab__content__messageBlock FeaturedTab__section">
                  <div className="FeaturedTab__content__primaryMessage">
                    {isPreorder ?
                      <FormattedMessage id="FeaturedTab.messageBlock.lastChance" defaultMessage="Pre-order now" />
                      :
                      <FormattedMessage id="FeaturedTab.messageBlock.outnow" defaultMessage="Out Now" />
                    }
                  </div>
                </div>
              </div>

              <div className="FeaturedTab__content__bottomContainer">
                <PlatformIcons hit={hit} colorOverride="dark" />
                <div className="FeaturedTab__content__bottomContainer__priceButton">
                  {!hit.presale && !hit.free_to_play && !hit.hide_price &&
                    <PriceContainer
                      type={hit.type}
                      discountPercent={hit.discount_percent}
                      price={hit.price}
                      tiered={hit.tiered}
                      hideDiscount={hit.hide_discount}
                      slug={hit.slug}
                      discountPercentOverride={hit.discount_percent_override}
                      fullPrice={hit.fullPrice}
                      mystery={hit.mystery}
                      drm={validDrm}
                      payWhatYouWant={hit.pay_what_you_want}
                    />
                  }
                  {!hit.tiered && !hit.presale && !hit.free_to_play &&
                    <AddToCartBtn
                      showInCartLink
                      id={hit.product_id}
                      slug={hit.slug}
                      voucherToAdd={hit.addVoucher}
                      hideButtonText={window.locale !== 'en' && isAboveMd}
                    />
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Link to={`/${hit.type}/${hit.slug}`} className="ProductsMegaMenu__tabButton" onClick={() => setMegaMenuTab('')}>
        <FormattedMessage id="featured.button.view" defaultMessage="View {productName}" values={{ productName: hit.name }} />
      </Link>
    </div>
  );
}

FeaturedTab.propTypes = {
  hit: PropTypes.shape({}),
  setMegaMenuTab: PropTypes.func,
  section: PropTypes.string,
  tab: PropTypes.string,
};

FeaturedTab.defaultProps = {
  hit: null,
  setMegaMenuTab: () => { },
  section: '',
  tab: '',
};
